// ============================================================================
// FACILITATOR PARTS — shared display pieces for the facilitator list, its
// detail drawer and the profile page
// ============================================================================

import React from 'react';
import { CheckCircle2, Circle, Trash2, Loader } from 'lucide-react';
import {
  SectorRef,
  FacilitatorTraining,
  FacilitatorTrainingEvent,
  FacilitatorTrainingStatus,
  TRAINING_STATUS_LABELS,
  TRAINING_STATUS_COLORS,
  TRAINING_EVENT_LABELS
} from '../../services/studio-bookings';

/** Accepts YYYY-MM-DD or a full ISO timestamp; renders e.g. "1 Oct 2026". */
export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function TrainingStatusBadge({ status }: { status: FacilitatorTrainingStatus }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold border ${TRAINING_STATUS_COLORS[status]}`}>
      {TRAINING_STATUS_LABELS[status]}
    </span>
  );
}

export function SectorChip({ sector, primary = false }: { sector: SectorRef; primary?: boolean }) {
  const tone = !sector.isActive
    ? 'bg-slate-50 text-slate-400 border-slate-200 line-through decoration-slate-300'
    : primary
      ? 'bg-primary/10 text-primary border-primary/30'
      : 'bg-white text-slate-600 border-slate-200';
  return (
    <span
      title={sector.isActive ? undefined : 'This sector is no longer active'}
      className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium border ${tone}`}
    >
      {sector.name}
      {!sector.isActive && <span className="ml-1 no-underline text-[10px] uppercase">(inactive)</span>}
    </span>
  );
}

export function SectorList({ primary, secondary }: { primary: SectorRef | null; secondary: SectorRef[] }) {
  if (!primary && secondary.length === 0) {
    return <p className="text-sm text-slate-400">No sectors tagged yet</p>;
  }
  return (
    <div className="space-y-2">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Primary</p>
        {primary ? <SectorChip sector={primary} primary /> : <span className="text-sm text-slate-400">—</span>}
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Secondary</p>
        {secondary.length > 0
          ? <div className="flex flex-wrap gap-1.5">{secondary.map(s => <SectorChip key={s.sectorId} sector={s} />)}</div>
          : <span className="text-sm text-slate-400">—</span>}
      </div>
    </div>
  );
}

function Stage({ done, label, detail }: { done: boolean; label: string; detail: string }) {
  return (
    <li className="flex items-start gap-2.5">
      {done
        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
        : <Circle className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />}
      <div>
        <p className={`text-sm font-semibold ${done ? 'text-navy' : 'text-slate-400'}`}>{label}</p>
        <p className="text-xs text-slate-500">{detail}</p>
      </div>
    </li>
  );
}

export function TrainingStages({ training }: { training: FacilitatorTraining }) {
  const assessmentDetail = training.assessedAt
    ? `${training.assessmentOutcome === 'pass' ? 'Passed' : 'Failed'} ${formatDate(training.assessedAt)}${
        training.nextAssessmentDue ? ` · reassess by ${formatDate(training.nextAssessmentDue)}` : ''}`
    : 'Not assessed';
  return (
    <div className="space-y-3">
      <TrainingStatusBadge status={training.status} />
      <ol className="space-y-2.5">
        <Stage
          done={!!training.walkthroughCompletedAt}
          label="Platform walkthrough"
          detail={training.walkthroughCompletedAt ? `Completed ${formatDate(training.walkthroughCompletedAt)}` : 'Not completed'}
        />
        <Stage
          done={!!training.inPersonTrainedAt}
          label="In-person training"
          detail={training.inPersonTrainedAt ? `Last attended ${formatDate(training.inPersonTrainedAt)}` : 'Not recorded'}
        />
        <Stage
          done={training.assessmentOutcome === 'pass'}
          label="Assessment"
          detail={assessmentDetail}
        />
      </ol>
    </div>
  );
}

export function TrainingHistory({
  events,
  deletingId,
  onDelete
}: {
  events: FacilitatorTrainingEvent[];
  deletingId?: string | null;
  onDelete?: (event: FacilitatorTrainingEvent) => void;
}) {
  if (events.length === 0) {
    return <p className="text-sm text-slate-400">No in-person training or assessments recorded</p>;
  }
  return (
    <ul className="divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl bg-white">
      {events.map(e => (
        <li key={e.eventId} className="px-3.5 py-2.5 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-navy">
              {TRAINING_EVENT_LABELS[e.type]}
              {e.outcome && (
                <span className={`ml-2 text-xs font-bold ${e.outcome === 'pass' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {e.outcome === 'pass' ? 'Pass' : 'Fail'}
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500">
              {formatDate(e.date)} · recorded by {e.recordedByName}
            </p>
            {e.notes && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{e.notes}</p>}
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(e)}
              disabled={deletingId === e.eventId}
              title="Delete entry"
              className="text-slate-400 hover:text-red-600 disabled:opacity-40 p-1"
            >
              {deletingId === e.eventId ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
